import { Claim } from '@interfaces/a-organizar/claim';
import { PermissionObject } from './permission-labels';
import { PermissionActionKind } from './permission-tree.models';

/**
 * Regras puras de uma claim: se é leitura ou gravação e qual rótulo a pessoa lê.
 * O código técnico (`LOANOPERATION_VIEW`) nunca vira rótulo; ele só aparece no tooltip.
 */

/** Ações que só leem: ver, listar, consultar, simular, gerar relatório sem gravar. */
const READ_ACTIONS = new Set([
  'MODULE',
  'VIEW',
  'LIST',
  'SELF',
  'REPORTS',
  'REPORTS_SCREEN',
  'DEBT_REPORT',
  'REPORT_HEDGE_POSITION',
  'SIMULATE',
  'PREVIEW',
  'EXPORT_PDF',
  'EXPORT_DAILY_REPORT',
  'COMPARE_STRUCTURES',
  'CALCULATOR',
  'MY_DEALS',
  'ALL_DEALS',
  'MY_EARNINGS',
  'RECEIVE_DERIVATIVE_NOTIFICATIONS',
  'KANBAN',
]);

/** Pedaços que, em qualquer posição do nome da ação, indicam leitura (`ATTACHMENT_LIST`, `VIEW_COMPANY`). */
const READ_TOKENS = new Set(['VIEW', 'LIST', 'MODULE']);

/** Pedaços que vencem os de leitura (`MANAGE_VIEWS` grava). */
const WRITE_TOKENS = new Set([
  'CREATE',
  'UPDATE',
  'DELETE',
  'MANAGE',
  'SAVE',
  'SEND',
  'SHARE',
  'APPLY',
  'IMPORT',
  'CONFIGURE',
  'APPROVE',
  'VOTE',
]);

/** Nome da ação dentro do grupo, em maiúsculas: `ATTACHMENT_LIST` de `LOANOPERATION_ATTACHMENT_LIST`. */
export function actionCode(claim: Pick<Claim, 'name' | 'value'>, groupName: string): string {
  const prefix = `${groupName.toUpperCase()}_`;
  if (claim.value?.toUpperCase().startsWith(prefix)) {
    return claim.value.substring(prefix.length).toUpperCase();
  }
  return (claim.name ?? '').toUpperCase();
}

/** Leitura × gravação. Claim de grupo `Menu*` é tratada à parte (`menu`). */
export function classifyAction(code: string): Exclude<PermissionActionKind, 'menu'> {
  const normalized = code.toUpperCase();
  if (READ_ACTIONS.has(normalized)) return 'read';

  const tokens = normalized.split('_');
  if (tokens.some(token => WRITE_TOKENS.has(token))) return 'write';
  if (tokens.some(token => READ_TOKENS.has(token))) return 'read';
  return 'write';
}

/** Rótulos curtos para as ações genéricas, que o backend descreve só com o código ou com uma palavra solta. */
const GENERIC_LABELS: Record<string, string> = {
  MODULE: 'Acessar o módulo',
  VIEW: 'Ver detalhes',
  LIST: 'Listar',
  CREATE: 'Criar',
  UPDATE: 'Editar',
  DELETE: 'Excluir',
  SELF: 'Acessar os próprios dados',
  SELF_UPDATE: 'Editar os próprios dados',
  ATTACHMENT: 'Anexar',
  ATTACHMENT_CREATE: 'Anexar arquivo',
  ATTACHMENT_UPDATE: 'Editar anexo',
  ATTACHMENT_DELETE: 'Excluir anexo',
  ATTACHMENT_VIEW: 'Ver anexo',
  ATTACHMENT_LIST: 'Listar anexos',
  ATTACHMENTS: 'Anexos',
  REPORTS: 'Relatórios',
  REPORTS_SCREEN: 'Tela de relatórios',
  SIMULATE: 'Simular',
  TRANSFER: 'Transferir para outra empresa',
  SEND_MAIL: 'Enviar e-mail',
  MANAGE: 'Gerenciar',
  KANBAN: 'Ver o kanban',
  DEBT_REPORT: 'Gerar relatório de dívida',
};

/** Descrições genéricas do backend (resx por nome de ação) que não dizem nada sozinhas. */
const GENERIC_DESCRIPTIONS = new Set(
  [
    'Anexo',
    'Atualizar',
    'Criar',
    'Deletar',
    'Excluir',
    'Listar',
    'Módulo',
    'Relatórios',
    'Simular',
    'Visualizar',
  ].map(text => text.toLowerCase())
);

function isCodeLike(text: string): boolean {
  return /^[A-Z0-9_]+$/.test(text.trim());
}

/** "VIEW_HEDGE_COSTS" → "View hedge costs" (último recurso, quando não há descrição nem dicionário). */
function humanizeCode(code: string): string {
  const words = code.toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface ActionText {
  label: string;
  hint: string;
}

/**
 * Rótulo e texto de apoio de uma claim. Usa a descrição do backend quando ela é específica; para as genéricas
 * (sem texto, ou com "Visualizar", "Listar"...), o dicionário curto em português.
 */
export function describeAction(
  claim: Pick<Claim, 'name' | 'value' | 'description'>,
  code: string,
  object: PermissionObject | null = null
): ActionText {
  const description = (claim.description ?? '').trim();
  const isGenericDescription =
    !description ||
    isCodeLike(description) ||
    description.toUpperCase() === code ||
    GENERIC_DESCRIPTIONS.has(description.toLowerCase());

  if (!isGenericDescription) {
    return { label: description, hint: '' };
  }

  const withObject = object ? describeWithObject(code, object) : null;
  if (withObject) return withObject;

  const generic = GENERIC_LABELS[code];
  if (generic) return { label: generic, hint: '' };

  return { label: humanizeCode(code), hint: '' };
}

/**
 * Ações genéricas com o objeto da tela: "Excluir empréstimo" e uma linha de apoio dizendo o que a pessoa consegue
 * fazer. Só para os códigos que o dicionário conhece; o resto cai no rótulo genérico.
 */
function describeWithObject(code: string, object: PermissionObject): ActionText | null {
  const { singular, plural, feminine } = object;
  const a = feminine ? 'uma' : 'um';
  const ofThe = feminine ? 'da' : 'do';
  const newWord = feminine ? 'nova' : 'novo';
  switch (code) {
    case 'MODULE':
      return { label: `Acessar ${plural}`, hint: `Abrir a área de ${plural} no sistema` };
    case 'LIST':
      return { label: `Listar ${plural}`, hint: `Ver a lista de ${plural}` };
    case 'VIEW':
      return {
        label: `Ver detalhe ${ofThe} ${singular}`,
        hint: `Abrir ${a} ${singular} e ver os dados`,
      };
    case 'CREATE':
      return { label: `Cadastrar ${singular}`, hint: `Criar ${a} ${singular} ${newWord}` };
    case 'UPDATE':
      return { label: `Editar ${singular}`, hint: `Alterar os dados de ${a} ${singular}` };
    case 'DELETE':
      return { label: `Excluir ${singular}`, hint: `Apagar ${a} ${singular}` };
    case 'ATTACHMENT_LIST':
      return { label: 'Listar anexos', hint: `Ver a lista de anexos ${ofThe} ${singular}` };
    case 'ATTACHMENT_VIEW':
      return { label: 'Ver anexo', hint: `Abrir e baixar um anexo ${ofThe} ${singular}` };
    case 'ATTACHMENT_CREATE':
      return {
        label: 'Incluir anexo',
        hint: `Anexar arquivos ${feminine ? 'à' : 'ao'} ${singular}`,
      };
    case 'ATTACHMENT_UPDATE':
      return {
        label: 'Editar anexo',
        hint: `Trocar o nome ou o arquivo de um anexo ${ofThe} ${singular}`,
      };
    case 'ATTACHMENT_DELETE':
      return { label: 'Excluir anexo', hint: `Remover um anexo ${ofThe} ${singular}` };
    default:
      return null;
  }
}

/** Grupo de visibilidade de menu (`MenuControl`, `MenuManagement`...). */
export function isMenuGroup(groupName: string): boolean {
  return /^Menu[A-Z]/.test(groupName);
}
